import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Line } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { AdditiveBlending, Color, Euler, Line3, Matrix4, Quaternion, Vector3 } from "three";
import FresnelMaterial from "./materials/FresnelMaterial";
import { useDrag } from "@use-gesture/react";
import { useSpring, animated, config as springConfig } from "@react-spring/three";

import { cameraStore } from "../../appStore";
import { MathUtils } from "three";

import { useSynchronizedState } from "@solidwhite/multiuser";

const fresnelColor = "#66cc99";
const fresnelBaseColor = "#367a58";

const sphereTarget = new Vector3();

const DragSphere = ({
  lineStart = new Vector3(-1, 0, 0),
  lineEnd = new Vector3(1, 0, 0),
  debug = false,
  onDrag = (value) => {},
  onComplete = (value) => {},
  planeRotation = 0,
  sphereRadius = 0.04,
  id = "",
  ...props
}) => {
  const dragPoint = useRef();
  const debugPointHit = useRef();
  const planeRef = useRef();
  const lineRef = useRef();
  const pulseSphereRef = useRef();

  const dragging = useRef(false);
  const [hovered, sethovered] = useSynchronizedState(`Dragsphere_${id}_hovered`, false);
  const [draggingState, setDraggingState] = useSynchronizedState(`Dragsphere_${id}_dragging`, false);

  const [dragValue, setdragValue] = useSynchronizedState(`Dragsphere_${id}`, 0);

  useEffect(() => {
    if (hovered) {
      setTimeout(() => {
        document.body.style.cursor = "grab";
      }, 16);
      sphereApi.start({ scale: 1.25 });
    } else {
      document.body.style.cursor = "auto";
      sphereApi.start({ scale: 1 });
    }
  }, [hovered]);

  const [sphereSpring, sphereApi] = useSpring(() => ({
    from: {
      scale: 0,
    },
    to: {
      scale: 1,
    },
    onChange: ({ value }) => {
      if (lineRef.current) {
        lineRef.current.material.opacity = value.scale * 0.4;
      }
    },
    config: springConfig.stiff,
  }));

  const [coneSpring, coneApi] = useSpring(() => ({
    from: {
      scale: 0,
    },
    to: {
      scale: 1,
    },
    config: springConfig.stiff,
  }));

  const pulse = useSpring(
    {
      loop: true,
      config: {
        duration: hovered ? 200 : 2500,
      },
      from: {
        scale: 1,
        opacity: hovered ? 0 : 0.75,
      },
      to: {
        scale: hovered ? 1 : 3.5,
        opacity: 0,
      },
      onChange: ({ value }) => {
        if (pulseSphereRef.current) {
          pulseSphereRef.current.scale.set(value.scale, value.scale, value.scale);
          pulseSphereRef.current.material.uniforms.opacity.value = value.opacity;
        }
      },
    },
    [hovered],
  );

  useEffect(() => {
    if (draggingState) dragging.current = true;
  }, [draggingState]);

  const [dragSpring, dragApi] = useSpring(
    () => ({
      drag: dragValue,
      onChange: ({ value }) => {
        onDrag(value.drag);
        setSphereRelativePosition(value.drag);
        if (value.drag > 0.95 && dragging.current) {
          onComplete();
          sethovered(false);
          document.body.style.cursor = "auto";
          dragging.current = false;
          setDraggingState(false);
          setdragValue(0);
        }
      },
      config: springConfig.stiff,
    }),
    [dragValue],
  );

  const { raycaster } = useThree();

  const linePoints = useMemo(() => {
    return [lineStart, lineEnd];
  }, []);

  const virtualLine = useMemo(() => {
    var line = new Line3(linePoints[0], linePoints[1]);
    return line;
  }, []);

  const [planePosition, planeQuaternion] = useMemo(() => {
    var pos = new Vector3();
    pos.copy(lineStart);
    pos.add(lineEnd.clone().sub(lineStart).multiplyScalar(0.5));

    var lineDirection = lineEnd.clone().sub(lineStart);
    var matrix = new Matrix4().lookAt(lineStart, lineEnd, new Vector3(0, 1, 0));

    // console.log(lineDirection);

    const quaternion = new Quaternion();
    quaternion.setFromRotationMatrix(matrix);
    var rotate90 = new Quaternion().setFromEuler(new Euler(Math.PI / 2, Math.PI, Math.PI / 2));
    quaternion.multiply(rotate90);

    return [pos, quaternion];
  }, []);

  useLayoutEffect(() => {
    if (dragPoint.current) dragPoint.current.position.copy(lineStart);
  }, []);

  useFrame(() => {
    if (dragPoint.current && dragging.current) {
      // console.log(raycaster);
      var hit = raycaster.intersectObjects([planeRef.current], true);
      if (hit.length > 0) {
        var target = new Vector3();
        virtualLine.closestPointToPoint(hit[0].point, true, target);

        debugPointHit.current.position.copy(hit[0].point);
        // dragPoint.current.position.copy(target);

        var dragValue = virtualLine.closestPointToPointParameter(hit[0].point, true);

        console.log(dragValue);

        setdragValue(dragValue);

        // onDrag(dragValue);

        // if (dragValue > 0.95) {
        //   onComplete();
        //   sethovered(false);
        //   document.body.style.cursor = "auto";
        // }
      }
    }
  });

  const setSphereRelativePosition = (value) => {
    console.log(value);
    let start = lineStart.clone();
    let end = lineEnd.clone();

    sphereTarget.copy(start);
    sphereTarget.add(end.sub(start).multiplyScalar(value));

    // sphereTarget.copy(start.add(end.sub(start)).multiplyScalar(value));
    dragPoint.current.position.copy(sphereTarget);
  };

  const bind = useDrag(({ down }) => {
    dragging.current = down;
    setDraggingState(down);
    cameraStore.enabled = !down;
    document.body.style.cursor = down ? "grabbing" : "auto";
    // if(down){
    // sphereApi.start({ scale: down ? 0 : 1 });
    // coneApi.start({ scale: down ? 0 : 1 });
    // }
  });

  useEffect(() => {
    sphereApi.start({ scale: draggingState ? 0 : 1 });
    coneApi.start({ scale: draggingState ? 0 : 1 });
  }, [draggingState]);

  return (
    <group>
      <Line
        points={linePoints}
        transparent
        depthTest={false}
        depthWrite={false}
        color={fresnelBaseColor}
        dashed={true}
        dashSize={0.01}
        gapSize={0.005}
        opacity={0.4}
        blending={AdditiveBlending}
        visible={true}
        ref={lineRef}
      />

      <group ref={dragPoint}>
        <animated.mesh visible={true} scale={sphereSpring.scale}>
          <sphereGeometry args={[sphereRadius]} />
          <FresnelMaterial
            depthTest={false}
            depthWrite={false}
            opacity={0.8}
            transparent
            uBaseColor={fresnelBaseColor}
            uFresnelColor={fresnelColor}
            uFresnelPower={0.5}
            // blending={AdditiveBlending}
          />
          <animated.mesh visible={true} scale={2} ref={pulseSphereRef}>
            <sphereGeometry args={[sphereRadius]} />
            <FresnelMaterial
              depthTest={false}
              depthWrite={false}
              opacity={0.1}
              transparent
              uBaseColor={new Color(fresnelBaseColor).multiplyScalar(0.5)}
              uFresnelColor={new Color(fresnelColor).multiplyScalar(0.8)}
              uFresnelPower={3}
              blending={AdditiveBlending}
            />
          </animated.mesh>
        </animated.mesh>

        <mesh
          visible={false}
          {...bind()}
          layers={[1]}
          onPointerOver={() => sethovered(true)}
          onPointerLeave={() => sethovered(false)}
        >
          <sphereGeometry args={[0.05]} />
        </mesh>
      </group>

      <mesh ref={debugPointHit} visible={debug ? true : false}>
        <sphereGeometry args={[0.009]} />
        <meshBasicMaterial color="blue" depthTest={false} depthWrite={false} transparent />
      </mesh>

      <group ref={planeRef} position={planePosition} quaternion={planeQuaternion}>
        <group rotation={[MathUtils.degToRad(planeRotation), 0, 0]}>
          <mesh renderOrder={11} visible={debug ? true : false}>
            <planeGeometry args={[2, 1]} />
            <meshBasicMaterial wireframe transparent color="yellow" depthTest={false} />
          </mesh>
          <mesh layers={[1]} visible={false}>
            <planeGeometry args={[2, 1]} />
            <meshBasicMaterial wireframe transparent color="yellow" />
          </mesh>
          <mesh layers={[1]} visible={false} rotation={[Math.PI, 0, 0]}>
            <planeGeometry args={[2, 1]} />
            <meshBasicMaterial wireframe transparent color="yellow" />
          </mesh>
        </group>
      </group>

      <group position={lineEnd} quaternion={planeQuaternion}>
        <animated.group scale={coneSpring.scale} position={[0, 0, 0]}>
          <mesh rotation={[0, 0, MathUtils.degToRad(90)]} position={[0.025, 0, 0]}>
            <coneGeometry args={[0.01, 0.05, 12]} />
            <FresnelMaterial
              depthTest={false}
              depthWrite={false}
              opacity={0.8}
              transparent
              uBaseColor={fresnelBaseColor}
              uFresnelColor={fresnelColor}
              uFresnelPower={0.5}
              // blending={AdditiveBlending}
            />
          </mesh>
        </animated.group>
      </group>

      {debug && (
        <group>
          <mesh position={lineStart}>
            <sphereGeometry args={[0.015]} />
            <meshBasicMaterial transparent color="green" depthWrite={false} depthTest={false} />
          </mesh>
          <mesh position={lineEnd}>
            <sphereGeometry args={[0.015]} />
            <meshBasicMaterial transparent color="red" depthWrite={false} depthTest={false} />
          </mesh>
        </group>
      )}
    </group>
  );
};

export default DragSphere;
